/**
 * Hook for fetching and managing cache data for playbook nodes.
 *
 * Fetches cache status for nodes that have playbook steps, and provides
 * methods to delete individual node caches or clear all caches.
 *
 * @module usePlaybookCache
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EffectivePlaybookData, NodeCacheData } from '../types/playbook-cache';
import { API_URL } from '../../../../config';

interface UsePlaybookCacheParams {
  workflowId: string;
  /** Node IDs to fetch cache for (typically nodes with playbook steps) */
  nodeIds: string[];
}

interface UsePlaybookCacheResult {
  cacheByNodeId: Map<string, NodeCacheData>;
  effectiveByNodeId: Map<string, EffectivePlaybookData>;
  inactiveByNodeId: Map<string, NodeCacheData>;
  isLoading: boolean;
  isError: boolean;
  deleteCacheForNode: (nodeId: string) => Promise<void>;
  deleteAllCaches: () => Promise<void>;
  refetch: () => void;
}

type CacheStatusResponse = {
  ok: true;
  nodes: NodeCacheData[];
  /** Canonical field. */
  effectivePlaybooks?: EffectivePlaybookData[];
  /** @deprecated Legacy alias of `effectivePlaybooks`, kept for deploy-skew tolerance (old API). */
  effectiveRecipes?: EffectivePlaybookData[];
};

function isCacheStatusResponse(value: unknown): value is CacheStatusResponse {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    record.ok === true &&
    Array.isArray(record.nodes) &&
    (Array.isArray(record.effectivePlaybooks) || Array.isArray(record.effectiveRecipes))
  );
}

export function usePlaybookCache({
  workflowId,
  nodeIds,
}: UsePlaybookCacheParams): UsePlaybookCacheResult {
  const [effectiveByNodeId, setEffectiveByNodeId] = useState<Map<string, EffectivePlaybookData>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    if (nodeIds.length === 0) {
      setEffectiveByNodeId(new Map());
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    void (async () => {
      try {
        const response = await fetch(`${API_URL}/workflows/${workflowId}/cache-status`, {
          method: 'GET',
          credentials: 'include',
        });
        const json: unknown = await response.json();
        if (!response.ok || !isCacheStatusResponse(json)) {
          if (!cancelled) setEffectiveByNodeId(new Map());
          if (!cancelled) setIsError(true);
          return;
        }

        const requestedNodeIds = new Set(nodeIds);
        const effectivePlaybooks = json.effectivePlaybooks ?? json.effectiveRecipes ?? [];
        if (!cancelled) {
          setEffectiveByNodeId(
            new Map(
              effectivePlaybooks
                .filter((playbook) => requestedNodeIds.has(playbook.nodeId))
                .map((playbook) => [playbook.nodeId, playbook])
            )
          );
        }
      } catch {
        if (!cancelled) setEffectiveByNodeId(new Map());
        if (!cancelled) setIsError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workflowId, nodeIds.join(','), fetchKey]);

  const deleteCacheForNode = useCallback(
    async (nodeId: string) => {
      await fetch(`${API_URL}/workflows/${workflowId}/nodes/${nodeId}/cache`, {
        method: 'DELETE',
        credentials: 'include',
      });
      refetch();
    },
    [workflowId, refetch]
  );

  const deleteAllCaches = useCallback(async () => {
    await fetch(`${API_URL}/workflows/${workflowId}/cache`, {
      method: 'DELETE',
      credentials: 'include',
    });
    refetch();
  }, [workflowId, refetch]);

  const { activeCacheByNodeId, inactiveByNodeId } = useMemo(() => {
    const active = new Map<string, NodeCacheData>();
    const inactive = new Map<string, NodeCacheData>();
    for (const playbook of effectiveByNodeId.values()) {
      if (playbook.activeCache) active.set(playbook.nodeId, playbook.activeCache);
      if (playbook.inactiveCache) inactive.set(playbook.nodeId, playbook.inactiveCache);
    }
    return { activeCacheByNodeId: active, inactiveByNodeId: inactive };
  }, [effectiveByNodeId]);

  return {
    cacheByNodeId: activeCacheByNodeId,
    effectiveByNodeId,
    inactiveByNodeId,
    isLoading,
    isError,
    deleteCacheForNode,
    deleteAllCaches,
    refetch,
  };
}
